import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../../core/api-client.service';
import { PageDataService } from '../../../core/page-data.service';
import { SiteConfigService } from '../../../core/site-config.service';
import { ServiceMarketplaceService } from '../../service-marketplace/service-marketplace.service';

export interface ShowcaseService {
  id: number;
  versionID?: number;
  slug: string;
  title: string;
  description: string;
  imageUrl?: string | null;
  price?: number | null;
  currency?: string;
  durationMinutes?: number;
  categoryID?: number;
  categoryTitle?: string;
  categoryDisplayOrder?: number;
  displayOrder?: number | null;
  providerCount?: number;
  instantAvailable?: boolean;
  quoteAvailable?: boolean;
}
export interface ShowcaseBranch {
  id: number;
  slug?: string;
  sellerID?: number;
  name: string;
  address: string;
  phone: string;
  email?: string;
  mapsLink?: string | null;
  imageUrl?: string | null;
  socialAccounts?: Array<{ id: number; platform: string; label: string; icon?: string; url: string }>;
  workingHours?: unknown;
}
export interface ShowcasePerson {
  id: number;
  branchStaffID?: number;
  slug: string;
  name: string;
  title: string;
  biography?: string;
  imageUrl?: string | null;
  branchIDs?: number[];
  branchStaffIDs?: number[];
  services?: Array<{ listingID?: number; id?: number; title?: string }>;
  workingHours?: unknown;
}
export interface AppointmentFormField {
  id: number;
  formKey: string;
  type: string;
  options?: Array<string | { value?: string; title?: string; label?: string }> | null;
  required?: boolean | number | string;
  minlenght?: number | string | null;
  maxlenght?: number | string | null;
  title?: string;
  description?: string;
}
export interface AppointmentForm {
  formID: number;
  fields: AppointmentFormField[];
}
export interface BrandShowcaseData {
  storeID: number;
  services: ShowcaseService[];
  packages?: Array<{ listingID:number; versionID?:number; slug:string; title:string; description:string; unitsTotal:number; mode:string; serviceListingID:number; validityDays:number; price:number; currency:string; imageUrl?:string|null; buyAction?:{type:string;listingID:number;buyNow:boolean} }>;
  branches: ShowcaseBranch[];
  team: ShowcasePerson[];
  appointmentForm: AppointmentForm;
}

type ApiResponse = {
  success?: boolean;
  result?: boolean;
  data?: BrandShowcaseData | { data?: BrandShowcaseData };
};

@Injectable({ providedIn: 'root' })
export class BrandShowcaseService {
  private readonly api = inject(ApiClientService);
  private readonly pageData = inject(PageDataService);
  private readonly site = inject(SiteConfigService);
  private readonly serviceMarketplace = inject(ServiceMarketplaceService);

  async data(): Promise<BrandShowcaseData> {
    await this.site.ready();
    if (this.site.config().businessMode === 'serviceMarketplace') {
      const services = await this.serviceMarketplace.catalog();
      return {
        storeID: Number(this.site.config().storeID ?? 0),
        services: services.map((item) => ({
          id: item.id,
          versionID: item.versionID,
          slug: item.slug,
          title: item.title,
          description: item.description,
          imageUrl: item.imageUrl,
          price: item.minPrice,
          currency: item.currency,
          durationMinutes: item.durationMinutes,
          providerCount: item.providerCount,
          instantAvailable: item.instantAvailable,
          quoteAvailable: item.quoteAvailable
        })),
        packages: [], branches: [],
        team: [],
        appointmentForm: { formID: 0, fields: [] }
      };
    }
    const seeded = this.pageData.get<ApiResponse>('brandShowcase');
    const seededData = this.unwrap(seeded);
    if (seededData) {
      return this.normalize(seededData);
    }
    const response = await firstValueFrom(this.api.post<ApiResponse>('brandShowcase', {}));
    return this.normalize(this.unwrap(response) ?? this.empty());
  }

  private normalize(data: BrandShowcaseData): BrandShowcaseData {
    return {
      ...data,
      services: (data.services ?? []).map((item) => ({ ...item, price: Number(item.price) > 0 ? item.price : null, imageUrl: this.media(item.imageUrl, item.versionID) })),
      packages: (data.packages ?? []).map((item) => ({...item,imageUrl:this.media(item.imageUrl,item.versionID)})),
      branches: (data.branches ?? []).map((item) => ({ ...item, imageUrl: this.media(item.imageUrl) })),
      team: (data.team ?? []).map((item) => ({ ...item, imageUrl: this.media(item.imageUrl) })),
      appointmentForm: {
        formID: Number(data.appointmentForm?.formID ?? 0),
        fields: data.appointmentForm?.fields ?? []
      }
    };
  }

  trackSocial(id: number): void {
    if (!id) return;
    this.api.post('brandShowcaseSocialClick', { id, utmSource: 'next-branch' }).subscribe({ error: () => undefined });
  }

  private unwrap(response?: ApiResponse | null): BrandShowcaseData | null {
    const first = response?.data;
    if (!first) return null;
    if ('storeID' in first) return first;
    return first.data ?? null;
  }

  private empty(): BrandShowcaseData {
    return { storeID: 0, services: [], packages: [], branches: [], team: [], appointmentForm: { formID: 0, fields: [] } };
  }

  private media(url?: string | null, versionID?: number): string | null {
    if (!url) return null;
    // Existing product/service image URLs may contain a stale [slug]. Keep
    // the stored path untouched and resolve only the requested image size.
    const prepared = url.replace('[size]', 'b');
    const absolute = /^https?:\/\//.test(prepared) ? prepared : `${this.site.config().apiBaseUrl}/${prepared.replace(/^\/+/, '')}`;
    return versionID ? `${absolute}${absolute.includes('?') ? '&' : '?'}v=${versionID}` : absolute;
  }
}
